import { titleCase } from '@common/helpers/strings';
import NextImage from '@react/shared/components/Image';
import groupByKey from '@react/shared/helpers/groupByKey';
import kebabToWords from '@react/shared/helpers/kebabToWords';
import sortByKey from '@react/shared/helpers/sortByKey';
import { useRouter } from 'next/router';
import React from 'react';
import { assetPath } from 'sly/lib/path';
import {
  getStateCountiesSitemapUrls,
  getStateSitemapUrls,
  getTocStateUrl,
} from 'sly/provider/helpers/getStateSitemapUrls';
import { usePrefetch } from 'sly/services/api';
import {
  SitemapLayout,
  SitemapLink,
  SitemapLinkWrapper,
} from 'sly/sitemap/components/SitemapSharedComponents';
import { sitemapAllStatesBreadCrumbItems } from 'sly/sitemap/constants';
import { generateSitemapWithEvents } from 'sly/sitemap/helpers';
import { Link } from 'sly/system';
import { Heading } from 'sly/system';

const StateSitemap = () => {
  const {
    query: { state },
  } = useRouter();
  const {
    requestInfo: { normalized: stateSitemap, hasFinished: stateHasFinished },
  } = usePrefetch('getSitemapState', {
    state,
  });

  const {
    requestInfo: {
      normalized: countiesSitemap,
      hasFinished: countiesHasFinished,
    },
  } = usePrefetch('getSitemapStateCounties', {
    state,
  });

  const {
    requestInfo: {
      normalized: communitiesSitemap,
      hasFinished: communitiesHasFinished,
    },
  } = usePrefetch('getSitemapCommunitiesByState', {
    state,
  });

  if (!stateHasFinished || !countiesHasFinished || !communitiesHasFinished) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <NextImage
          width='1'
          height='1'
          alt=''
          src={assetPath('uploads/react-assets/homebase/loader.svg')}
        />
      </div>
    );
  }

  const stateName = kebabToWords(state);

  const groudCityByCareType = groupByKey(
    getStateSitemapUrls(stateSitemap, titleCase(state)),
    'typeCare'
  );
  const groupedCounties = groupByKey(
    getStateCountiesSitemapUrls(countiesSitemap, titleCase(state)),
    'typeCare'
  );

  return (
    <SitemapLayout
      headTitle={`${stateName} Site Map | Seniorly`}
      breadcrumbItems={generateSitemapWithEvents([
        ...sitemapAllStatesBreadCrumbItems,
        { path: `/sitemap/states/${state}`, label: stateName },
      ])}
      metaDescription={`Sitemap of all ${stateName} pages on Seniorly. Find the ${stateName} page you are looking for.`}
    >
      <Heading as='h2' className='mb-8'>
        {stateName} Sitemap
      </Heading>
      <>
        {Object.entries(groudCityByCareType).map(([careType, cities]) => {
          return (
            <div key={careType}>
              <Heading as='h4' font='font-t-m' className='mb-6'>
                <Link href={getTocStateUrl(careType, state)}>
                  {careType} in {stateName}
                </Link>
              </Heading>
              <SitemapLinkWrapper>
                {sortByKey(cities, 'city.name').map(({ title, path }) => {
                  return (
                    <SitemapLink href={path} key={title}>
                      {title}
                    </SitemapLink>
                  );
                })}
              </SitemapLinkWrapper>
            </div>
          );
        })}
        {Object.entries(groupedCounties).map(([careType, counties]) => {
          return (
            <div key={careType}>
              <Heading as='h4' font='font-t-m' className='mb-6'>
                {careType} By Counties in {stateName}
              </Heading>
              <SitemapLinkWrapper>
                {counties.map(({ title, path }) => {
                  return (
                    <SitemapLink href={path} key={title}>
                      {title}
                    </SitemapLink>
                  );
                })}
              </SitemapLinkWrapper>
            </div>
          );
        })}
        <Heading as='h4' font='font-t-m' className='mb-6'>
          List of {stateName} Senior Living Communities
        </Heading>
        <SitemapLinkWrapper>
          {sortByKey(communitiesSitemap, 'name').map(({ name, url }) => {
            return (
              <SitemapLink href={url} key={name}>
                {name}
              </SitemapLink>
            );
          })}
        </SitemapLinkWrapper>
      </>
    </SitemapLayout>
  );
};

export default StateSitemap;
